import { DocumentReference } from 'firebase/firestore';
import { type  Effect } from './effect';
import { type  Property, PropertyType, getDefaultProperty } from './property';

export enum FeatureType {
	biology = 'biology',
	core = 'core',
	coreAbility = 'coreAbility',
	corp = 'corp',
	feat = 'feat',
	homeWorld = 'homeWorld',
	knack = 'knack',
	legacy = 'legacy',
	legacyChoices = 'legacyChoices',
	legacyItem = 'legacyItem',
	origin = 'origin',
	quirk = 'quirk',
	senses = 'senses',
	skill = 'skill',
	skillBoon = 'skillBoon',
	skillTrick = 'skillTrick',
	stats= 'stats',
	talent = 'talent',
	weaponTrick = 'weaponTrick',
}

// Filter-only types that don't have their own collections or entity lists
export const FILTER_ONLY_TYPES: FeatureType[] = [
	FeatureType.coreAbility,
	FeatureType.feat,
	FeatureType.knack,
	FeatureType.quirk,
	FeatureType.skillBoon,
	FeatureType.skillTrick,
	FeatureType.stats,
	FeatureType.talent,
	FeatureType.weaponTrick,
];

export const isFilterOnlyType = (type: FeatureType): boolean => {
	return FILTER_ONLY_TYPES.includes(type);
};

export interface FeatureRule {
	minQty: number;
	maxQty: number;
}

export interface SelectionRules {
	min: number;
	max: number;
}

export interface Feature {
	id: string | undefined;
	name: string;
	type: FeatureType;
	imageURL: string;
	docURL: string;
	description: string;
	subFeatures: Feature[];
	subFeatureIds: string[];
	sortOrder: number;
	qty: number;
	effectRefs?: DocumentReference[];
	effects?: Effect[];
	selectionRules?: SelectionRules;
	memberRefs?: DocumentReference[];
	/**
	 * Optional tag-cloud gate for whether this feature is shown at all.
	 * Uses the same meetsRequires() semantics as Property.requires.
	 * Example: ['mutant'] hides the feature until the character has the mutant tag.
	 */
	requires?: string[];
	/** Bundle-hydrated: string IDs for lazy effect resolution (replaces effectRefs) */
	_effectRefIds?: string[];
	/** Bundle-hydrated: string IDs for member lookup (replaces memberRefs) */
	_memberRefIds?: string[];
}

export interface FeatureOption {
	id: string;
	name: string;
	type: FeatureType;
	imageURL: string;
	tag: string;
	description: string;
	docURL: string;
	qty?: number;
	skillGroup?: string;
}

export const getDefaultFeatureOption = (): FeatureOption => {
    return {
        id: '',
        name: '',
        type: FeatureType.origin,
        imageURL: '',
        tag: '',
        description: '',
        docURL: '',
        qty: 1,
    };
};

export const defaultFeature = (): Feature => {
	return {
		id: undefined,
		name: '',
		type: FeatureType.origin,
		imageURL: '',
		docURL: '',
		description: '',
		subFeatures: [],
		subFeatureIds: [],
		sortOrder: 0,
		qty: 1,
		effectRefs: [],
		effects: [],
		selectionRules: { min: 0, max: 0 },
	};
};

export type FeatureOptions = FeatureOption[];

export function featureTypeToPropertyType(featureType: FeatureType): PropertyType | undefined {
    if (Object.values(PropertyType).includes(featureType as unknown as PropertyType)) {
        return featureType as unknown as PropertyType;
    }
    return undefined;
}

export function propertyTypeToFeatureType(propertyType: PropertyType): FeatureType | undefined {
    if (Object.values(FeatureType).includes(propertyType as unknown as FeatureType)) {
        return propertyType as unknown as FeatureType;
    }
    return undefined;
}

function feature2Property(feature: Feature): Property {
    const defaultProperty = getDefaultProperty();
    
    return {
        ...defaultProperty,
        id: '-1', // Assuming a default of '-1' if undefined
        name: feature.name,
        description: feature.description,
        imageURL: feature.imageURL,
        docURL: feature.docURL,
        type: featureTypeToPropertyType(feature.type) ?? PropertyType.item, // Convert feature type to property type or use a default
        // Set other fields as needed, or use default values from getDefaultProperty
    };
}

export function featureOption2Property(featureOption: FeatureOption): Property {
    const defaultProperty = getDefaultProperty();

	function title(str: string) {
		return str.replace(/(^|\s)\S/g, function(t) { return t.toUpperCase() });
	  }

    return {
        ...defaultProperty,
        id: featureOption.id,
        name: title(`${featureOption.name} ${featureOption.type}`),
        description: featureOption.description,
        imageURL: featureOption.imageURL,
        docURL: featureOption.docURL,
        type: featureTypeToPropertyType(featureOption.type) ?? PropertyType.item, // Convert feature type to property type or use a default
        // Default values or transformations for other Property-specific fields
    };
}

/**
 * Generate a feature ID from blueprint type, rank, and name
 * Format: {blueprintType}-{rank}-{name-slug}
 */
export function generateFeatureId(blueprintType: string, rank: number, name: string): string {
	const nameSlug = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
	return `${blueprintType}-${rank}-${nameSlug}`;
}